/**
 * Beat 7, once Rosalie has cleared the farm.
 *
 * These are deliberately made from shaped noise rather than oscillators. The
 * field must have distance and air, not a hidden score pad; the one bird is a
 * distant, band-limited texture rather than a tuned musical figure.
 */

import { connectChain, filter, gain, noiseSource } from '../synth';
import type { SoundDefinition } from '../types';

/** Schedule one closed noise gesture without leaving a step at either edge. */
function pulse(
  shape: GainNode,
  t0: number,
  start: number,
  attack: number,
  hold: number,
  release: number,
  level: number,
): void {
  shape.gain.setValueAtTime(0, t0 + start);
  shape.gain.linearRampToValueAtTime(level, t0 + start + attack);
  shape.gain.setValueAtTime(level, t0 + start + attack + hold);
  shape.gain.linearRampToValueAtTime(0, t0 + start + attack + hold + release);
}

/**
 * Keep a loop's broadband material safely away from its own wrap.
 *
 * `noiseSource()` joins its buffer, but its output still needs to meet zero at
 * the render boundary. The quiet endpoint is intentionally brief: this is a
 * seam guard, not an audible pulse in the field bed.
 */
function loopEnvelope(ctx: BaseAudioContext, duration: number, level: number): GainNode {
  const shape = gain(ctx, 0);
  const t0 = ctx.currentTime;
  shape.gain.setValueAtTime(0, t0);
  shape.gain.linearRampToValueAtTime(level, t0 + 0.018);
  shape.gain.setValueAtTime(level, t0 + duration - 0.03);
  shape.gain.linearRampToValueAtTime(0, t0 + duration - 0.012);
  return shape;
}

/**
 * A slow gain contour whose cycles arrive at the same phase at a loop wrap.
 *
 * It is intentionally a gain envelope, not a free-running LFO: the individual
 * swells remain organic in their filtered noise while their exact period makes
 * the loop reproducible and prevents a once-per-loop click.
 */
function periodicSwell(
  ctx: BaseAudioContext,
  duration: number,
  cycles: number,
  floor: number,
  crest: number,
): GainNode {
  const shape = gain(ctx, floor);
  const t0 = ctx.currentTime;
  const period = duration / cycles;
  for (let cycle = 0; cycle < cycles; cycle++) {
    const start = t0 + cycle * period;
    shape.gain.setValueAtTime(floor, start);
    shape.gain.linearRampToValueAtTime(crest, start + period * 0.42);
    shape.gain.linearRampToValueAtTime(floor, start + period);
  }
  return shape;
}

const boardsBreak: SoundDefinition = {
  id: 'boards-break',
  label: 'Rotten boards giving way',
  description: 'One close, wet-softwood double crack followed by a small dry clatter.',
  category: 'foley',
  duration: 0.9,
  loop: false,
  render(ctx) {
    const t0 = ctx.currentTime;
    const out = gain(ctx, 0.72);
    connectChain(out, ctx.destination);

    const firstCrack = gain(ctx, 0);
    pulse(firstCrack, t0, 0.018, 0.0012, 0.006, 0.105, 0.48);
    connectChain(
      noiseSource(ctx, 0.9, 1201),
      filter(ctx, { type: 'highpass', frequency: 420, q: 0.72 }),
      filter(ctx, { type: 'lowpass', frequency: 6100, q: 0.68 }),
      firstCrack,
      out,
    );

    const secondCrack = gain(ctx, 0);
    // The 43 ms offset is the second fracture travelling across a soaked board,
    // not a rhythmic double tap. It is what keeps the impact out of gunshot.
    pulse(secondCrack, t0, 0.061, 0.0015, 0.005, 0.082, 0.35);
    connectChain(
      noiseSource(ctx, 0.9, 1207),
      filter(ctx, { type: 'highpass', frequency: 300, q: 0.74 }),
      filter(ctx, { type: 'lowpass', frequency: 4700, q: 0.72 }),
      secondCrack,
      out,
    );

    const clatter = gain(ctx, 0);
    const pieces: readonly [number, number, number][] = [
      [0.18, 0.12, 0.12],
      [0.305, 0.086, 0.095],
      [0.474, 0.058, 0.075],
    ];
    for (const [offset, level, release] of pieces) {
      pulse(clatter, t0, offset, 0.002, 0.008, release, level);
    }
    connectChain(
      noiseSource(ctx, 0.9, 1213),
      // Leaving the clatter in the low-mid makes pieces of damp timber rather
      // than bright glass, while the closed envelopes keep the open field dry.
      filter(ctx, { type: 'bandpass', frequency: 760, q: 0.92 }),
      clatter,
      out,
    );
  },
};

const rollGrass: SoundDefinition = {
  id: 'roll-grass',
  label: 'Rolling on wet turf',
  description: 'A soft low-mid turf rush with slow wet grass swells, kept below breathing.',
  category: 'foley',
  duration: 8,
  loop: true,
  render(ctx) {
    const out = gain(ctx, 0.31);
    connectChain(out, ctx.destination);

    connectChain(
      noiseSource(ctx, 8, 1223),
      filter(ctx, { type: 'highpass', frequency: 75, q: 0.7 }),
      filter(ctx, { type: 'lowpass', frequency: 980, q: 0.78 }),
      loopEnvelope(ctx, 8, 0.24),
      out,
    );

    // Four two-second swells complete exactly four cycles in the eight-second
    // loop. This is flattened grass returning, not a beat or a rolling tyre.
    connectChain(
      noiseSource(ctx, 8, 1229),
      filter(ctx, { type: 'bandpass', frequency: 430, q: 0.72 }),
      periodicSwell(ctx, 8, 4, 0.028, 0.108),
      loopEnvelope(ctx, 8, 1),
      out,
    );

    // A narrow, low wetness layer supplies the faint suck without promoting it
    // to droplets, which would make the run read as a puddle instead of turf.
    connectChain(
      noiseSource(ctx, 8, 1237),
      filter(ctx, { type: 'bandpass', frequency: 185, q: 1.1 }),
      periodicSwell(ctx, 8, 4, 0.008, 0.03),
      loopEnvelope(ctx, 8, 1),
      out,
    );
  },
};

const ditchWater: SoundDefinition = {
  id: 'ditch-water',
  label: 'Ditch water — entry and wading',
  description: 'A compact muddy entry splash with disturbed drain water settling close by.',
  category: 'foley',
  duration: 2.2,
  loop: false,
  render(ctx) {
    const t0 = ctx.currentTime;
    const out = gain(ctx, 0.49);
    connectChain(out, ctx.destination);

    const entry = gain(ctx, 0);
    pulse(entry, t0, 0.022, 0.006, 0.045, 0.3, 0.28);
    connectChain(
      noiseSource(ctx, 2.2, 1241),
      // The drain's earth walls and mud remove a pool splash's brittle top end.
      filter(ctx, { type: 'highpass', frequency: 110, q: 0.72 }),
      filter(ctx, { type: 'lowpass', frequency: 1950, q: 0.72 }),
      entry,
      out,
    );

    const water = gain(ctx, 0);
    const disturbed: readonly [number, number, number, number][] = [
      [0.19, 0.03, 0.34, 0.13],
      [0.66, 0.045, 0.42, 0.102],
      [1.24, 0.04, 0.48, 0.071],
    ];
    for (const [offset, attack, release, level] of disturbed) {
      pulse(water, t0, offset, attack, 0.04, release, level);
    }
    connectChain(
      noiseSource(ctx, 2.2, 1249),
      filter(ctx, { type: 'bandpass', frequency: 570, q: 0.84 }),
      water,
      out,
    );
  },
};

const fieldAir: SoundDefinition = {
  id: 'field-air',
  label: 'Open field bed',
  description: 'Open hedge wind with one sparse, distant bird and no tonal bed.',
  category: 'machine',
  duration: 16,
  loop: true,
  render(ctx) {
    const t0 = ctx.currentTime;
    const out = gain(ctx, 0.28);
    connectChain(out, ctx.destination);

    // Four four-second swells complete exactly four cycles in the sixteen-second
    // loop. Their low crest and broadband source make a hedge moving in wind,
    // rather than a rhythmic or tonal foreground gesture.
    connectChain(
      noiseSource(ctx, 16, 1259),
      filter(ctx, { type: 'highpass', frequency: 95, q: 0.68 }),
      filter(ctx, { type: 'lowpass', frequency: 1850, q: 0.68 }),
      periodicSwell(ctx, 16, 4, 0.052, 0.126),
      loopEnvelope(ctx, 16, 1),
      out,
    );

    connectChain(
      noiseSource(ctx, 16, 1271),
      filter(ctx, { type: 'bandpass', frequency: 1150, q: 0.68 }),
      periodicSwell(ctx, 16, 4, 0.009, 0.035),
      loopEnvelope(ctx, 16, 1),
      out,
    );

    const bird = gain(ctx, 0);
    // One brief, band-limited breath of noise is deliberately distant and
    // unpitched. It says there is a world beyond the hedge without becoming a
    // melodic figure or a repeated dawn chorus.
    pulse(bird, t0, 6.43, 0.026, 0.075, 0.115, 0.042);
    connectChain(
      noiseSource(ctx, 16, 1277),
      filter(ctx, { type: 'bandpass', frequency: 3150, q: 3.2 }),
      bird,
      out,
    );
  },
};

export const outSounds: readonly SoundDefinition[] = [
  boardsBreak,
  rollGrass,
  ditchWater,
  fieldAir,
];
